import { useState, useEffect, useCallback, useRef } from 'react';
import { updateUserPreferences } from '../services/userProfile';

// Theme mode options
export type ThemeMode = 'light' | 'dark' | 'system';

// Resolved theme (what's actually applied)
export type ResolvedTheme = 'light' | 'dark';

// localStorage key
const THEME_STORAGE_KEY = 'theme';

// CSS class for dark mode (light is default)
const DARK_MODE_CLASS = 'dark-mode';

/**
 * Get system preference using media query
 */
function getSystemPreference(): ResolvedTheme {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/**
 * Get stored theme preference from localStorage
 */
function getStoredTheme(): ThemeMode {
  if (typeof window === 'undefined') return 'light';
  const stored = localStorage.getItem(THEME_STORAGE_KEY);
  if (stored === 'light' || stored === 'dark' || stored === 'system') {
    return stored;
  }
  return 'light'; // Default to light mode
}

/**
 * Apply theme to document
 */
function applyTheme(resolvedTheme: ResolvedTheme): void {
  if (typeof document === 'undefined') return;

  if (resolvedTheme === 'dark') {
    document.body.classList.add(DARK_MODE_CLASS);
  } else {
    document.body.classList.remove(DARK_MODE_CLASS);
  }

  // Also set color-scheme for native elements (scrollbars, form controls)
  document.documentElement.style.colorScheme = resolvedTheme;
}

/**
 * Resolve the actual theme based on mode
 */
function resolveTheme(mode: ThemeMode): ResolvedTheme {
  if (mode === 'system') {
    return getSystemPreference();
  }
  return mode;
}

export interface UseThemeReturn {
  /** Current theme mode setting */
  themeMode: ThemeMode;
  /** Set theme mode */
  setThemeMode: (mode: ThemeMode) => void;
  /** The actual resolved theme being applied */
  resolvedTheme: ResolvedTheme;
  /** Toggle between light and dark (ignores system) */
  toggleTheme: () => void;
  /** Whether system preference is being used */
  isSystemPreference: boolean;
  /** Initialize theme from user profile preferences */
  initializeFromProfile: (preferences: { theme?: ThemeMode } | null) => void;
}

/**
 * Hook for managing application theme
 *
 * Features:
 * - Persists preference to localStorage AND user_profiles.preferences
 * - Supports system preference detection
 * - Listens for system preference changes
 * - Applies theme via CSS class on body
 */
export function useTheme(): UseThemeReturn {
  const [themeMode, setThemeModeState] = useState<ThemeMode>(() => getStoredTheme());
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() =>
    resolveTheme(getStoredTheme())
  );
  const initializedFromProfile = useRef(false);

  // Initialize theme from user profile (called after login)
  const initializeFromProfile = useCallback((preferences: { theme?: ThemeMode } | null) => {
    if (initializedFromProfile.current) return;

    const profileTheme = preferences?.theme;
    if (profileTheme && (profileTheme === 'light' || profileTheme === 'dark' || profileTheme === 'system')) {
      initializedFromProfile.current = true;
      setThemeModeState(profileTheme);
      localStorage.setItem(THEME_STORAGE_KEY, profileTheme);
      const resolved = resolveTheme(profileTheme);
      setResolvedTheme(resolved);
      applyTheme(resolved);
    }
  }, []);

  // Set theme mode and persist to localStorage + user profile
  const setThemeMode = useCallback((mode: ThemeMode) => {
    setThemeModeState(mode);
    localStorage.setItem(THEME_STORAGE_KEY, mode);

    const resolved = resolveTheme(mode);
    setResolvedTheme(resolved);
    applyTheme(resolved);

    // Persist to user profile (fire and forget)
    updateUserPreferences({ theme: mode }).catch(err => {
      console.warn('Failed to save theme to profile:', err);
    });
  }, []);

  // Toggle between light and dark
  const toggleTheme = useCallback(() => {
    const newMode: ThemeMode = resolvedTheme === 'dark' ? 'light' : 'dark';
    setThemeMode(newMode);
  }, [resolvedTheme, setThemeMode]);

  // Apply theme on mount and listen for system preference changes
  useEffect(() => {
    // Apply initial theme
    const resolved = resolveTheme(themeMode);
    setResolvedTheme(resolved);
    applyTheme(resolved);

    // Listen for system preference changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: light)');

    const handleChange = () => {
      if (themeMode === 'system') {
        const newResolved = getSystemPreference();
        setResolvedTheme(newResolved);
        applyTheme(newResolved);
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [themeMode]);

  return {
    themeMode,
    setThemeMode,
    resolvedTheme,
    toggleTheme,
    isSystemPreference: themeMode === 'system',
    initializeFromProfile,
  };
}

export default useTheme;
